/**
 * The one hand-off out of the setup wizard, shared by both hosts.
 *
 * Why this exists rather than two near-identical copies:
 *
 * Index.handleOnboardingComplete and DemoApp.handleWizardComplete did the same
 * four things in the same order — build the app state, merge it over anything
 * stored, persist, then flip the view. Being copies, they drifted, and the drift
 * went the dangerous way: DemoApp persisted `isComplete: true`; Index did not,
 * relying on SetupComplete having already done it.
 *
 * SetupComplete does — except on its early return. `if (!schoolCreds) {
 * onContinue(); return; }` fires before the save, and getSchoolCredentials()
 * returns null whenever localStorage lost them: cleared site data, a resumed
 * session, private browsing hitting quota. In that case Index reached
 * `viewState === "app" && onboardingState.isComplete` with isComplete false,
 * fell through, and re-rendered the wizard — the user sits on the completion
 * screen with no error and nothing happening. That is the same symptom this
 * path has produced three times, waiting on a fourth cause.
 *
 * Persisting here makes the flip unconditional: whatever route the user took to
 * get here, arriving means onboarding is done. The hosts keep only what is
 * genuinely theirs — which view to show next.
 */
import { buildAppStateFromOnboarding, getAppState, saveAppState } from './app-store';
import { getOnboardingState, saveOnboardingState, type OnboardingState } from './onboarding-store';

/**
 * Persist everything the app needs to mount, and return the fresh onboarding
 * state. Callers flip their own view afterwards.
 *
 * Deliberately synchronous and total: no awaits, so there is nothing here that
 * can fail to settle. The screens downstream of this call are where every
 * previous version of this bug lived.
 */
export function completeOnboardingHandoff(): OnboardingState {
  const fresh = buildAppStateFromOnboarding();
  const stored = getAppState();
  // Merge rather than replace: a returning user's lessons and settings live in
  // the stored state, and only the school block is rebuilt from the wizard.
  saveAppState(stored ? { ...stored, school: fresh.school } : fresh);

  // The line Index was missing. Idempotent, so the hosts that already did it
  // (and SetupComplete's normal path) are unaffected.
  saveOnboardingState({ isComplete: true });

  return getOnboardingState();
}
